import { graphql } from "@generated/client";
import {
    type AnswerUpsertInput,
    type CreateSupplierTemplateQuestionsFromFileInput,
    type CreateSupplierUserInput,
    type ExportSupplierQuestionnaireOptionsInput,
    type OpenQuestionnaireInput,
    type OpenQuestionnairesInput,
    type PaginationInput,
    type QuestionnaireFilterInput,
    type QuestionnaireLocaleInput,
    type SendSupplierQuestionnaireEmailInput,
    type SendSupplierQuestionnaireEmailsInput,
    type SupplierFilter,
    type SupplierQuestionnaireGroupCreateInput,
    type SupplierQuestionnaireGroupUpdateInput,
    type SupplierSort,
    type SupplierTemplateQuestionCreateInput,
    type SupplierTemplateQuestionFilterInput,
    type SupplierTemplateQuestionUpdateInput,
    type SupplierTemplateQuestionUpdateOrderInput,
    type UpdateSupplierInput,
} from "@generated/client/graphql";
import { AccessHeadersHolder } from "../AccessHeadersHolder";
import { graphqlClientKoyeb } from "../clients";

const GetSupplierQuery = graphql(`
    query GetSupplier($id: String!) {
        supplier(id: $id) {
            id
            name
            tags {
                id
                name
                color
            }
            users {
                id
                email
                firstName
                lastName
                department
                avatar {
                    signedUrl
                }
            }
        }
    }
`);

const GetUserSupplierQuery = graphql(`
    query GetUserSupplier {
        userSupplier {
            id
        }
    }
`);

const GetSupplierQuestionnaireTemplatesQuery = graphql(`
    query getSupplierQuestionnaireTemplates {
        supplierQuestionnaireTemplates {
            id
            name
        }
    }
`);

const GetSupplierQuestionnairesQuery = graphql(`
    query GetSupplierQuestionnaires(
        $supplierId: ID!
        $filter: QuestionnaireFilterInput
        $pagination: PaginationInput!
    ) {
        supplierQuestionnaires2(
            supplierId: $supplierId
            filter: $filter
            pagination: $pagination
        ) {
            list: data {
                id
                name
                createdAt
                deadline
                supplierId
                locale {
                    availableLocales
                    default
                }
                stats {
                    total
                    answered
                    validated
                }
                sentBy {
                    firstName
                    lastName
                    avatar {
                        signedUrl
                    }
                }
            }
            count
            skip
            take
        }
    }
`);

const GetSupplierQuestionnaireQuery = graphql(`
    query GetSupplierQuestionnaire($questionnaireId: ID!, $supplierId: ID!) {
        supplierQuestionnaire2(
            questionnaireId: $questionnaireId
            supplierId: $supplierId
        ) {
            id
            name
            createdAt
            deadline
            status
            stats {
                total
                answered
                validated
            }
            locale {
                availableLocales
                default
            }
        }
    }
`);

const GetSupplierQuestionnaireQuestionsQuery = graphql(`
    query GetSupplierQuestionnaireQuestions(
        $questionnaireId: ID!
        $supplierId: ID!
        $pagination: PaginationInput
    ) {
        questionnaire: supplierQuestionnaire2(
            questionnaireId: $questionnaireId
            supplierId: $supplierId
        ) {
            id
            questions(pagination: $pagination) {
                data {
                    id
                    nameMultiLocale {
                        en
                        fr
                    }
                    descriptionMultiLocale {
                        en
                        fr
                    }
                    type
                    isRequired
                    questionGroupId
                    metadata {
                        options {
                            name {
                                en
                                fr
                            }
                            proof
                        }
                        unit
                        dependsOn {
                            questionId
                            displayIfSelected
                        }
                        proof
                        enableNaOption
                    }
                    answer {
                        id
                        questionId
                        value
                        comment
                        status
                        evidenceFiles {
                            id
                            name
                            signedUrl
                            mimeType
                        }
                    }
                }
            }
        }
    }
`);

const GetSupplierQuestionnaireGroupsQuery = graphql(`
    query GetSupplierQuestionnaireGroups(
        $questionnaireId: ID!
        $supplierId: ID!
    ) {
        questionnaire: supplierQuestionnaire2(
            questionnaireId: $questionnaireId
            supplierId: $supplierId
        ) {
            groups {
                id
                name {
                    en
                    fr
                }
                shortName {
                    en
                    fr
                }
                level
            }
        }
    }
`);

const UpsertSupplierQuestionnaireQuestionAnswerMutation = graphql(`
    mutation UpsertSupplierQuestionnaireQuestionAnswer(
        $input: AnswerUpsertInput!
    ) {
        upsertSupplierQuestionnaireAnswer(input: $input) {
            status
        }
    }
`);

const GetSuppliersQuery_deprecated = graphql(`
    query GetSuppliers_deprecated {
        suppliers_deprecated {
            id
            name
            tags {
                id
                name
            }
            users {
                id
                email
                firstName
                lastName
            }
            lastQuestionnaire {
                id
                status
                createdAt
                closedAt
                lastUpdatedAt
                lastReminderSentAt
                template {
                    id
                    name
                }
                stats {
                    total
                    answered
                    progress
                }
            }
        }
    }
`);

const GetSuppliersQuery = graphql(`
    query GetSuppliers(
        $filter: SupplierFilter
        $pagination: PaginationInput
        $orderBy: SupplierSort
    ) {
        suppliers(filter: $filter, pagination: $pagination, orderBy: $orderBy) {
            list: data {
                id
                name
                tags {
                    id
                    name
                    color
                }
                users {
                    id
                    email
                    firstName
                    lastName
                }
                lastQuestionnaire: lastQuestionnaire2 {
                    id
                    name
                    createdAt
                    updatedAt
                    deadline
                    status
                    stats {
                        total
                        answered
                        validated
                    }
                }
            }
            count
        }
    }
`);

const GetSuppliersMinimalQuery = graphql(`
    query GetSuppliersMinimal(
        $filter: SupplierFilter
        $pagination: PaginationInput
        $orderBy: SupplierSort
    ) {
        suppliers(filter: $filter, pagination: $pagination, orderBy: $orderBy) {
            list: data {
                id
                lastQuestionnaire: lastQuestionnaire2 {
                    id
                    status
                }
            }
        }
    }
`);

const DeleteSuppliersMutation = graphql(`
    mutation DeleteSuppliers($ids: [ID!]!) {
        deleteSuppliers(ids: $ids) {
            status
        }
    }
`);

const GetSupplierQuestionnaireNamesQuery = graphql(`
    query GetSupplierQuestionnaireNames {
        supplierQuestionnaireNames
    }
`);

const GetTemplateQuestionsQuery = graphql(`
    query GetTemplateQuestions(
        $filter: SupplierTemplateQuestionFilterInput
        $pagination: PaginationInput
    ) {
        supplierTemplateQuestions(filter: $filter, pagination: $pagination) {
            list: data {
                id
                questionnaireId
                nameMultiLocale {
                    en
                    fr
                }
                descriptionMultiLocale {
                    en
                    fr
                }
                type
                group {
                    id
                    name {
                        en
                        fr
                    }
                }
                tags {
                    id
                    name
                }
                order
            }
            count
        }
    }
`);

const GetTemplateQuestionsMinimalQuery = graphql(`
    query getTemplateQuestionsMinimal(
        $filter: SupplierTemplateQuestionFilterInput
        $pagination: PaginationInput
    ) {
        supplierTemplateQuestions(filter: $filter, pagination: $pagination) {
            list: data {
                id
            }
        }
    }
`);

const GetTemplateQuestionGroupsQuery = graphql(`
    query GetTemplateQuestionGroups {
        supplierTemplateQuestionGroups {
            id
            name {
                en
                fr
            }
        }
    }
`);

const UpdateSupplierMutation = graphql(`
    mutation UpdateSupplier($input: UpdateSupplierInput!) {
        updateSupplier(input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const GetSupplierTemplateQuestionnaireQuery = graphql(`
    query GetSupplierTemplateQuestionnaire {
        supplierTemplate {
            id
            isGenerating
            locale {
                availableLocales
                default
            }
        }
    }
`);

const CreateSupplierTemplateQuestionMutation = graphql(`
    mutation CreateSupplierTemplateQuestion(
        $input: SupplierTemplateQuestionCreateInput!
    ) {
        createSupplierTemplateQuestion(input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const CreateSupplierTemplateMutation = graphql(`
    mutation CreateSupplierTemplate($input: QuestionnaireLocaleInput!) {
        createSupplierTemplate(input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const CreateSupplierTemplateQuestionGroupMutation = graphql(`
    mutation CreateSupplierTemplateQuestionGroup(
        $input: SupplierQuestionnaireGroupCreateInput!
    ) {
        createQuestionGroup(input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const UpdateSupplierTemplateMutation = graphql(`
    mutation UpdateSupplierTemplate($input: QuestionnaireLocaleInput!) {
        updateSupplierTemplate(input: $input) {
            status
        }
    }
`);

const UpdateSupplierTemplateQuestionMutation = graphql(`
    mutation UpdateSupplierTemplateQuestion(
        $id: String!
        $input: SupplierTemplateQuestionUpdateInput!
    ) {
        updateSupplierTemplateQuestion(id: $id, input: $input) {
            status
            data {
                id
            }
        }
    }
`);

const UpdateSupplierTemplateQuestionsMutation = graphql(`
    mutation UpdateSupplierTemplateQuestions(
        $ids: [ID!]!
        $input: SupplierTemplateQuestionUpdateInput!
    ) {
        updateSupplierTemplateQuestions(ids: $ids, input: $input) {
            status
        }
    }
`);

const UpdateSupplierTemplateQuestionsOrderMutation = graphql(`
    mutation UpdateSupplierTemplateQuestionsOrder(
        $input: [SupplierTemplateQuestionUpdateOrderInput!]!
    ) {
        updateSupplierTemplateQuestionsOrder(input: $input) {
            status
        }
    }
`);

const UpdateSupplierTemplateQuestionGroupMutation = graphql(`
    mutation UpdateSupplierTemplateQuestionGroup(
        $input: SupplierQuestionnaireGroupUpdateInput!
    ) {
        updateQuestionGroup(input: $input) {
            status
        }
    }
`);

const DeleteSupplierTemplateQuestionMutation = graphql(`
    mutation DeleteSupplierTemplateQuestion($id: ID!) {
        deleteSupplierTemplateQuestion(id: $id) {
            status
        }
    }
`);

const DeleteSupplierTemplateQuestionsMutation = graphql(`
    mutation DeleteSupplierTemplateQuestions($ids: [ID!]!) {
        deleteSupplierTemplateQuestions(ids: $ids) {
            status
        }
    }
`);

const DeleteSupplierTemplateQuestionGroupMutation = graphql(`
    mutation DeleteSupplierTemplateQuestionGroup($id: ID!) {
        deleteSupplierTemplateQuestionGroup(id: $id) {
            status
        }
    }
`);

const OpenSupplierQuestionnaireMutation = graphql(`
    mutation OpenQuestionnaire($input: OpenQuestionnaireInput!) {
        openQuestionnaire(input: $input) {
            status
        }
    }
`);

const OpenSupplierQuestionnairesMutation = graphql(`
    mutation OpenQuestionnaires($input: OpenQuestionnairesInput!) {
        openQuestionnaires(input: $input) {
            status
        }
    }
`);

const SendSupplierQuestionnaireEmailMutation = graphql(`
    mutation SendSupplierQuestionnaireEmail(
        $input: SendSupplierQuestionnaireEmailInput!
    ) {
        sendSupplierQuestionnaireEmail(input: $input) {
            status
            error {
                code
                message
            }
        }
    }
`);

const SendSupplierQuestionnaireEmailsMutation = graphql(`
    mutation SendSupplierQuestionnaireEmails(
        $input: SendSupplierQuestionnaireEmailsInput!
    ) {
        sendSupplierQuestionnaireEmails(input: $input) {
            status
            error {
                code
                message
            }
        }
    }
`);

const CloseSupplierQuestionnaireMutation = graphql(`
    mutation CloseSupplierQuestionnaire($questionnaireId: ID!) {
        closeQuestionnaire(questionnaireId: $questionnaireId) {
            status
        }
    }
`);

const CloseSupplierQuestionnairesMutation = graphql(`
    mutation CloseSupplierQuestionnaires($questionnaireIds: [ID!]!) {
        closeQuestionnaires(questionnaireIds: $questionnaireIds) {
            status
        }
    }
`);

const ReOpenSupplierQuestionnaireMutation = graphql(`
    mutation ReOpenSupplierQuestionnaire($questionnaireId: ID!) {
        reOpenQuestionnaire(questionnaireId: $questionnaireId) {
            status
        }
    }
`);

const ReOpenSupplierQuestionnairesMutation = graphql(`
    mutation ReOpenSupplierQuestionnaires($questionnaireIds: [ID!]!) {
        reOpenQuestionnaires(questionnaireIds: $questionnaireIds) {
            status
        }
    }
`);

const CreateSupplierUsersMutation = graphql(`
    mutation CreateSupplierUsers(
        $supplierId: ID!
        $input: [CreateSupplierUserInput!]!
    ) {
        createSupplierUsers(supplierId: $supplierId, input: $input) {
            status
        }
    }
`);

const ExportSupplierQuestionnaireMutation = graphql(`
    mutation ExportSupplierQuestionnaire(
        $questionnaireId: ID!
        $options: ExportSupplierQuestionnaireOptionsInput!
    ) {
        exportSupplierQuestionnaire(
            questionnaireId: $questionnaireId
            options: $options
        ) {
            status
            data {
                signedUrl
            }
        }
    }
`);

const ExportTemplateQuestionsMutation = graphql(`
    mutation ExportTemplateQuestions {
        exportTemplateQuestions {
            status
            data {
                signedUrl
            }
        }
    }
`);

const ExportSuppliersListMutation = graphql(`
    mutation ExportSuppliersList {
        exportSuppliersList {
            status
            data {
                signedUrl
            }
        }
    }
`);

const ExportLastQuestionnaireOfSuppliersMutation = graphql(`
    mutation ExportLastQuestionnaireOfSuppliers(
        $options: ExportSupplierQuestionnaireOptionsInput!
    ) {
        exportLastQuestionnaireOfSuppliers(options: $options) {
            status
            data {
                signedUrl
            }
        }
    }
`);

const ExportLastQuestionnaireFilesOfSuppliersMutation = graphql(`
    mutation ExportLastQuestionnaireFilesOfSuppliers {
        exportLastQuestionnaireFilesOfSuppliers {
            status
            data {
                signedUrl
            }
        }
    }
`);

const DownloadSuppliersImportTemplateMutation = graphql(`
    mutation DownloadSuppliersImportTemplate {
        downloadSuppliersImportTemplate {
            status
            data {
                signedUrl
            }
        }
    }
`);

const DownloadQuestionsCreationTemplateMutation = graphql(`
    mutation DownloadQuestionsCreationTemplateMutation {
        downloadQuestionsCreationTemplate {
            status
            data {
                signedUrl
            }
        }
    }
`);

const CreateSupplierQuestionsFromFileMutation = graphql(`
    mutation CreateSupplierQuestionsFromFile(
        $input: CreateSupplierTemplateQuestionsFromFileInput!
    ) {
        createSupplierQuestionsFromFile(input: $input) {
            status
            data {
                createdQuestionsCount
                questionsInError
                errors
            }
        }
    }
`);

export class GraphQLSupplierRepository {
    async getSupplier(id: string) {
        return graphqlClientKoyeb.request(
            GetSupplierQuery,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getUserSupplier() {
        return graphqlClientKoyeb.request(
            GetUserSupplierQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSuppliers(
        filter: SupplierFilter,
        pagination: PaginationInput,
        orderBy: SupplierSort,
    ) {
        return graphqlClientKoyeb.request(
            GetSuppliersQuery,
            { filter, pagination, orderBy },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSuppliersMinimal(
        filter: SupplierFilter,
        pagination: PaginationInput,
        orderBy: SupplierSort,
    ) {
        return graphqlClientKoyeb.request(
            GetSuppliersMinimalQuery,
            { filter, pagination, orderBy },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async deleteSuppliers(ids: string[]) {
        return graphqlClientKoyeb.request(
            DeleteSuppliersMutation,
            { ids },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierQuestionnaireNames() {
        return graphqlClientKoyeb.request(
            GetSupplierQuestionnaireNamesQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierQuestionnaireTemplates_deprecated() {
        return graphqlClientKoyeb.request(
            GetSupplierQuestionnaireTemplatesQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierTemplateQuestionnaire() {
        return graphqlClientKoyeb.request(
            GetSupplierTemplateQuestionnaireQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierQuestionnaire(
        questionnaireId: string,
        supplierId: string,
    ) {
        return graphqlClientKoyeb.request(
            GetSupplierQuestionnaireQuery,
            { questionnaireId, supplierId },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierQuestionnaireQuestions(
        questionnaireId: string,
        supplierId: string,
        pagination?: PaginationInput,
    ) {
        return graphqlClientKoyeb.request(
            GetSupplierQuestionnaireQuestionsQuery,
            { questionnaireId, supplierId, pagination },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierQuestionnaires(
        supplierId: string,
        filter: QuestionnaireFilterInput,
        pagination: PaginationInput,
    ) {
        return graphqlClientKoyeb.request(
            GetSupplierQuestionnairesQuery,
            { supplierId, filter, pagination },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getTemplateQuestions(
        filter?: SupplierTemplateQuestionFilterInput,
        pagination?: PaginationInput,
    ) {
        return graphqlClientKoyeb.request(
            GetTemplateQuestionsQuery,
            { filter, pagination },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getTemplateQuestionsMinimal(
        filter?: SupplierTemplateQuestionFilterInput,
        pagination?: PaginationInput,
    ) {
        return graphqlClientKoyeb.request(
            GetTemplateQuestionsMinimalQuery,
            { filter, pagination },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getTemplateQuestionGroups() {
        return graphqlClientKoyeb.request(
            GetTemplateQuestionGroupsQuery,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSuppliers_deprecated() {
        return graphqlClientKoyeb.request(
            GetSuppliersQuery_deprecated,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async createSupplierTemplate(input: QuestionnaireLocaleInput) {
        return graphqlClientKoyeb.request(
            CreateSupplierTemplateMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async updateSupplierTemplate(input: QuestionnaireLocaleInput) {
        return graphqlClientKoyeb.request(
            UpdateSupplierTemplateMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async updateSupplier(input: UpdateSupplierInput) {
        return graphqlClientKoyeb.request(
            UpdateSupplierMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async createSupplierTemplateQuestion(
        input: SupplierTemplateQuestionCreateInput,
    ) {
        return graphqlClientKoyeb.request(
            CreateSupplierTemplateQuestionMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async createSupplierTemplateQuestionGroup(
        input: SupplierQuestionnaireGroupCreateInput,
    ) {
        return graphqlClientKoyeb.request(
            CreateSupplierTemplateQuestionGroupMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async updateSupplierTemplateQuestion(
        id: string,
        input: SupplierTemplateQuestionUpdateInput,
    ) {
        return graphqlClientKoyeb.request(
            UpdateSupplierTemplateQuestionMutation,
            { id, input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async updateSupplierTemplateQuestions(
        ids: string[],
        input: SupplierTemplateQuestionUpdateInput,
    ) {
        return graphqlClientKoyeb.request(
            UpdateSupplierTemplateQuestionsMutation,
            { ids, input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async updateSupplierTemplateQuestionsOrder(
        input: SupplierTemplateQuestionUpdateOrderInput[],
    ) {
        return graphqlClientKoyeb.request(
            UpdateSupplierTemplateQuestionsOrderMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async updateSupplierTemplateQuestionGroup(
        input: SupplierQuestionnaireGroupUpdateInput,
    ) {
        return graphqlClientKoyeb.request(
            UpdateSupplierTemplateQuestionGroupMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async deleteSupplierTemplateQuestion(id: string) {
        return graphqlClientKoyeb.request(
            DeleteSupplierTemplateQuestionMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async deleteSupplierTemplateQuestions(ids: string[]) {
        return graphqlClientKoyeb.request(
            DeleteSupplierTemplateQuestionsMutation,
            { ids },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async deleteSupplierTemplateQuestionGroup(id: string) {
        return graphqlClientKoyeb.request(
            DeleteSupplierTemplateQuestionGroupMutation,
            { id },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async openSupplierQuestionnaire(input: OpenQuestionnaireInput) {
        return graphqlClientKoyeb.request(
            OpenSupplierQuestionnaireMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async openSupplierQuestionnaires(input: OpenQuestionnairesInput) {
        return graphqlClientKoyeb.request(
            OpenSupplierQuestionnairesMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async sendSupplierQuestionnaireEmail(
        input: SendSupplierQuestionnaireEmailInput,
    ) {
        return graphqlClientKoyeb.request(
            SendSupplierQuestionnaireEmailMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async sendSupplierQuestionnaireEmails(
        input: SendSupplierQuestionnaireEmailsInput,
    ) {
        return graphqlClientKoyeb.request(
            SendSupplierQuestionnaireEmailsMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async closeSupplierQuestionnaire(questionnaireId: string) {
        return graphqlClientKoyeb.request(
            CloseSupplierQuestionnaireMutation,
            { questionnaireId },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async closeSupplierQuestionnaires(questionnaireIds: string[]) {
        return graphqlClientKoyeb.request(
            CloseSupplierQuestionnairesMutation,
            { questionnaireIds },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async reOpenSupplierQuestionnaire(questionnaireId: string) {
        return graphqlClientKoyeb.request(
            ReOpenSupplierQuestionnaireMutation,
            { questionnaireId },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async reOpenSupplierQuestionnaires(questionnaireIds: string[]) {
        return graphqlClientKoyeb.request(
            ReOpenSupplierQuestionnairesMutation,
            { questionnaireIds },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async upsertSupplierQuestionnaireQuestionAnswer(input: AnswerUpsertInput) {
        return graphqlClientKoyeb.request(
            UpsertSupplierQuestionnaireQuestionAnswerMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async createSupplierUsers(
        supplierId: string,
        input: CreateSupplierUserInput[],
    ) {
        return graphqlClientKoyeb.request(
            CreateSupplierUsersMutation,
            { supplierId, input },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async exportTemplateQuestions() {
        return graphqlClientKoyeb.request(
            ExportTemplateQuestionsMutation,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async exportSupplierQuestionnaire(
        questionnaireId: string,
        options: ExportSupplierQuestionnaireOptionsInput,
    ) {
        return graphqlClientKoyeb.request(
            ExportSupplierQuestionnaireMutation,
            { questionnaireId, options },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async exportSuppliersList() {
        return graphqlClientKoyeb.request(
            ExportSuppliersListMutation,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async exportLastQuestionnaireOfSuppliers(
        options: ExportSupplierQuestionnaireOptionsInput,
    ) {
        return graphqlClientKoyeb.request(
            ExportLastQuestionnaireOfSuppliersMutation,
            { options },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async exportLastQuestionnaireFilesOfSuppliers() {
        return graphqlClientKoyeb.request(
            ExportLastQuestionnaireFilesOfSuppliersMutation,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async downloadSuppliersImportTemplate() {
        return graphqlClientKoyeb.request(
            DownloadSuppliersImportTemplateMutation,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async getSupplierQuestionnaireGroups(
        questionnaireId: string,
        supplierId: string,
    ) {
        return graphqlClientKoyeb.request(
            GetSupplierQuestionnaireGroupsQuery,
            { questionnaireId, supplierId },
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async downloadQuestionsCreationTemplate() {
        return graphqlClientKoyeb.request(
            DownloadQuestionsCreationTemplateMutation,
            {},
            await AccessHeadersHolder.getHeaders(),
        );
    }

    async createSupplierQuestionsFromFile(
        input: CreateSupplierTemplateQuestionsFromFileInput,
    ) {
        return graphqlClientKoyeb.request(
            CreateSupplierQuestionsFromFileMutation,
            { input },
            await AccessHeadersHolder.getHeaders(),
        );
    }
}
